import { capture } from "../instrument";

export const DWT_CONTAINER_ID = "dwtcontrolContainer";
const DWT_RESOURCES_PATH = "/dwt";
const DWT_SCRIPT_URLS = ["/dwt/dynamsoft.webtwain.initiate.js", "/dwt/dynamsoft.webtwain.config.js"];

export const SCANNER_STORAGE_KEY = "admission:scannerName";
export const EXTRACTION_STEPS = ["Préparation de votre document…", "Reconnaissance du texte…", "Extraction des informations…", "Finalisation…"];

let dwtScriptsPromise = null;
let webTwainPromise = null;

const loadDwtScripts = () => {
  if (dwtScriptsPromise) return dwtScriptsPromise;

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = false;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Impossible de charger ${src}`));
      document.head.appendChild(script);
    });

  dwtScriptsPromise = (async () => {
    for (const url of DWT_SCRIPT_URLS) {
      await loadScript(url);
    }
  })();

  dwtScriptsPromise.catch(() => {
    dwtScriptsPromise = null;
  });

  return dwtScriptsPromise;
};

const getWebTwain = () => {
  if (webTwainPromise) return webTwainPromise;

  webTwainPromise = (async () => {
    await loadDwtScripts();

    return new Promise((resolve, reject) => {
      const { Dynamsoft } = window;
      if (!Dynamsoft?.DWT) return reject(new Error("Le scanner n'a pas pu être initialisé"));

      Dynamsoft.DWT.ResourcesPath = DWT_RESOURCES_PATH;
      Dynamsoft.DWT.UseLocalService = true;
      Dynamsoft.DWT.IfUseSSL = false;
      Dynamsoft.DWT.Containers = [{ WebTwainId: "dwtAdmin", ContainerId: DWT_CONTAINER_ID, Width: 0, Height: 0 }];

      Dynamsoft.DWT.RegisterEvent("OnWebTwainReady", () => resolve(Dynamsoft.DWT.GetWebTwain("dwtAdmin")));
      Dynamsoft.DWT.RegisterEvent("OnWebTwainError", (e) => reject(new Error(e?.message || "Erreur du scanner")));

      Dynamsoft.DWT.Load();
    });
  })();

  webTwainPromise.catch(() => {
    webTwainPromise = null;
  });

  return webTwainPromise;
};

const getScannerSources = async () => {
  const DWObject = await getWebTwain();
  const sources = await DWObject.GetSourceNamesAsync();
  return sources || [];
};

export const scanToJpeg = async (scannerName) => {
  const DWObject = await getWebTwain();

  const sources = await DWObject.GetSourceNamesAsync();
  if (!sources || sources.length === 0) throw new Error("Aucun scanner disponible");

  const matchedIndex = scannerName ? sources.findIndex((name) => name === scannerName) : -1;
  DWObject.SelectSourceByIndex(matchedIndex >= 0 ? matchedIndex : 0);
  DWObject.OpenSource();
  DWObject.RemoveAllImages();

  try {
    await DWObject.AcquireImageAsync({
      IfShowUI: false,
      PixelType: window.Dynamsoft.DWT.EnumDWT_PixelType.TWPT_GRAY,
      Resolution: 300,
    });
  } finally {
    DWObject.CloseSource();
  }

  if (DWObject.HowManyImagesInBuffer === 0) return null;

  const blob = await new Promise((resolve, reject) => DWObject.ConvertToBlob([0], window.Dynamsoft.DWT.EnumDWT_ImageType.IT_JPG, resolve, reject));
  DWObject.RemoveAllImages();
  return new File([blob], "scan.jpg", { type: "image/jpeg" });
};

export const DWT_INSTALLER_URL = "https://updater-app.cellar-fr-north-hds-c1.services.clever-cloud.com/DynamsoftServiceSetup.msi";

const SCANNER_SERVICE_UNAVAILABLE = /not installed|not running|not started|service (is )?stopped|failed to connect|connection refused|unreachable/i;

export const isScannerServiceMissing = (error) => SCANNER_SERVICE_UNAVAILABLE.test(error?.message || "");

export const loadScannerList = async () => {
  let sources = [];
  let serviceNotInstalled = false;
  try {
    sources = await getScannerSources();
  } catch (error) {
    if (isScannerServiceMissing(error)) serviceNotInstalled = true;
    else capture(error);
  }
  const storedName = localStorage.getItem(SCANNER_STORAGE_KEY) || "";
  const scannerName = sources.includes(storedName) ? storedName : sources[0] || "";
  return { scanners: sources, scannerName, serviceNotInstalled };
};
